/**
 * iter87.465.20 — MLS Play-In Bonus pick modal (NEW primitive,
 * renamed from "Play-In Pick" in iter87.465.23). One pick PER
 * CONFERENCE of who wins that conference's 8v9 Wild Card game, locked
 * alongside Champions/Dark Horse at Wild Card kickoff. Flat points per
 * correct conference pick — NOT part of the in-season Series
 * Predictor.
 *
 * iter87.467 (user-requested — floating Save button on every
 * predictor/Dark Horse modal, MLS Play-In Bonus specifically called
 * out) — tapping a team used to save instantly; now it only drafts a
 * selection per conference, and a floating "Save Picks" footer button
 * (always visible) commits whichever conference(s) changed.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, ListChecks, Info, Check } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";

export default function MlsPlayinPickModal({ open, onClose, onSaved, tournamentId, participantId, existingPicks, correctPoints = 6, incorrectPoints = -2 }) {
    const [savedPicks, setSavedPicks] = useState(existingPicks || {});
    const [draftPicks, setDraftPicks] = useState(existingPicks || {});
    const [bracket, setBracket] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !tournamentId) return;
        setSavedPicks(existingPicks || {});
        setDraftPicks(existingPicks || {});
        api.getMlsBracket(tournamentId).then(setBracket).catch(() => setBracket(null));
    }, [open, tournamentId, existingPicks]);

    if (!open) return null;
    const wc = (bracket?.rounds || []).find((r) => r.key === "wildcard");
    const series = (wc?.series || []).filter((s) => s.team_a_id && s.team_b_id);
    const locked = !!bracket?.pre_season_locked;
    const teamsById = {};
    (bracket?.teams || []).forEach((t) => { teamsById[t.id] = t; });
    const conferences = series.map((s) => s.conference);
    const dirtyConfs = conferences.filter((c) => draftPicks[c] !== savedPicks[c]);
    const savedCount = conferences.filter((c) => savedPicks[c]).length;

    const onSavePicks = async () => {
        if (locked || dirtyConfs.length === 0) return;
        setSaving(true);
        try {
            await Promise.all(dirtyConfs.map((c) => api.submitMlsPlayinPick(tournamentId, participantId, c, draftPicks[c])));
            setSavedPicks({ ...savedPicks, ...Object.fromEntries(dirtyConfs.map((c) => [c, draftPicks[c]])) });
            onSaved?.(draftPicks);
            toast.success("Play-In picks saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save your Play-In picks");
        } finally {
            setSaving(false);
        }
    };

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="mls-playin-pick-modal"
            backdropClassName="lg:items-center lg:justify-center lg:p-4"
            panelClassName="rounded-2xl border border-heat/40 bg-obsidian max-w-lg w-full max-h-[90dvh] overflow-hidden flex flex-col"
        >
            <div className="flex items-center justify-between gap-2 px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-white/[0.06] shrink-0">
                <div className="min-w-0 flex items-center gap-2 flex-1">
                    <div className="w-9 h-9 rounded-xl bg-heat flex items-center justify-center shrink-0">
                        <ListChecks className="w-4 h-4 text-black" strokeWidth={2.5} />
                    </div>
                    <h3 className="font-display font-extrabold text-lg uppercase truncate">Play-In Bonus</h3>
                </div>
                <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full bg-white/[0.06] hover:bg-white/[0.12] flex items-center justify-center transition-colors active:scale-95 shrink-0"
                    data-testid="close-mls-playin-pick-modal-btn"
                    aria-label="Close"
                >
                    <ChevronRight className="w-4 h-4 rotate-90" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">

            <div className="text-[11px] text-zinc-500 mb-4 flex gap-2 items-start text-left" data-testid="mls-playin-pick-explainer">
                <Info className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <p className="leading-relaxed">
                    <span className="text-white font-bold uppercase tracking-tight">How it works:</span>{" "}
                    Pick the winner of EACH conference's 8v9 Wild Card game. Locks alongside Champions Pick + Dark Horse Pick at kickoff — a correct pick is +{correctPoints} pts, an incorrect one is {incorrectPoints} pts, flat per conference (not part of the in-season Series Predictor).
                </p>
            </div>

            {locked && (
                <p data-testid="mls-playin-locked-notice" className="text-heat text-[11px] uppercase tracking-widest font-bold mb-3">
                    Locked — the Wild Card games have started. Your picks below are final.
                </p>
            )}

            {series.length > 0 ? (
                <div className="space-y-4" data-testid="mls-playin-conference-list">
                    {series.map((s) => {
                        const draftId = draftPicks[s.conference];
                        const savedTeam = savedPicks[s.conference] ? teamsById[savedPicks[s.conference]] : null;
                        return (
                            <div key={s.id} data-testid={`mls-playin-conference-${s.conference}`}>
                                <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-2">
                                    {s.conference} Conference — {savedTeam ? <span className="text-heat">Saved: {savedTeam.name}</span> : "no pick saved"}
                                </p>
                                <div className="grid grid-cols-2 gap-2">
                                    {[s.team_a_id, s.team_b_id].map((tid) => {
                                        const t = teamsById[tid];
                                        const isSelected = draftId === tid;
                                        return (
                                            <motion.button
                                                key={tid}
                                                data-testid={`mls-playin-${s.conference}-option-${tid}`}
                                                disabled={locked}
                                                onClick={() => !locked && setDraftPicks((p) => ({ ...p, [s.conference]: tid }))}
                                                whileHover={!locked ? { y: -2, scale: 1.015 } : {}}
                                                whileTap={!locked ? { scale: 0.975 } : {}}
                                                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                                className={`relative p-3 rounded-lg border text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                                                    isSelected
                                                        ? "border-heat bg-heat/[0.08]"
                                                        : "border-white/[0.08] bg-[#141414] hover:bg-[#181818] hover:border-heat/40"
                                                }`}
                                            >
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <TeamSwatch name={t?.name} sport="mls" size="w-7 h-7 text-[9px]" />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-bold truncate flex items-center gap-1">{t?.name || "TBD"}{isSelected && <Check className="w-3 h-3 text-heat shrink-0" />}</div>
                                                        <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mt-0.5">Seed #{t?.seed}</div>
                                                    </div>
                                                </div>
                                            </motion.button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p className="text-xs text-zinc-500 rounded-lg border border-white/[0.08] bg-white/[0.02] p-3" data-testid="mls-playin-no-matchups">
                    Wild Card matchups aren't set yet — check back once the regular season wraps.
                </p>
            )}

            </div>

            <div className="shrink-0 glass border-t border-white/10 px-4 sm:px-6 py-3" data-testid="mls-playin-pick-footer">
                <div className="flex items-center justify-between gap-3">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-zinc-400" data-testid="mls-playin-pick-count">
                        {dirtyConfs.length > 0 ? `${dirtyConfs.length} unsaved` : `${savedCount}/${conferences.length || 2} saved`}
                    </span>
                    <button
                        type="button"
                        disabled={saving || dirtyConfs.length === 0 || locked}
                        onClick={onSavePicks}
                        data-testid="mls-playin-save-btn"
                        className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-black bg-heat text-black font-display font-extrabold uppercase tracking-wide text-xs px-4 min-h-[44px] transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:pointer-events-none"
                    >
                        <Check className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save Picks"}
                    </button>
                </div>
            </div>
        </BentoModal>
    );
}
